import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import type { Size } from '../types.js';
import styles from './styles.js';

export type BadgeSeverity = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'contrast';
export type BadgeSize     = Size;

/**
 * uwc-badge
 *
 * A dual-mode badge component:
 *
 *  • **Standalone** — no slot content → renders as a coloured pill or dot indicator.
 *      `<uwc-badge value="8" severity="danger"></uwc-badge>`
 *
 *  • **Overlay** — slot content provided → wraps the child and positions the badge
 *    in the top-right corner.
 *      `<uwc-badge severity="danger"><uwc-icon name="bell"></uwc-icon></uwc-badge>`
 *
 * @attr {string}       value    - Badge label. Omit (or leave empty) for a dot indicator.
 * @attr {BadgeSeverity} severity - Color: primary | secondary | success | info | warning | danger | contrast.
 * @attr {BadgeSize}    size     - Standalone size: small | medium | large. Default: medium.
 *
 * @slot (default) - When populated, switches to overlay mode.
 *
 * @cssprop --uwc-badge-primary-bg      - Primary background.
 * @cssprop --uwc-badge-primary-color   - Primary text color.
 * @cssprop --uwc-badge-secondary-bg    - Secondary background.
 * @cssprop --uwc-badge-success-bg      - Success background.
 * @cssprop --uwc-badge-info-bg         - Info background.
 * @cssprop --uwc-badge-warning-bg      - Warning background.
 * @cssprop --uwc-badge-danger-bg       - Danger background.
 * @cssprop --uwc-badge-contrast-bg     - Contrast background.
 * @cssprop --uwc-badge-font-size       - Badge font size. Default: 0.6875rem.
 */
export class UwcBadge extends LitElement {

  static override styles: CSSResultGroup = styles;

  @property({ reflect: true }) value    = '';
  @property({ reflect: true }) severity: BadgeSeverity = 'primary';
  @property({ reflect: true }) size: BadgeSize = 'medium';

  @state() private _slotted = false;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override updated() {
    this.toggleAttribute('data-overlay', this._slotted);
  }

  // ── Slot detection ────────────────────────────────────────────────────────

  private _onSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._slotted = slot.assignedNodes({ flatten: true }).length > 0;
  }

  // ── Badge element ─────────────────────────────────────────────────────────

  private _badge() {
    const isDot = this.value === '';

    const classes = [
      'uwc-badge',
      this.severity !== 'primary' ? `uwc-badge--${this.severity}` : '',
      this.size !== 'medium'      ? `uwc-badge--${this.size}`     : '',
      isDot                       ? 'uwc-badge--dot'              : '',
    ].filter(Boolean).join(' ');

    return html`
      <span class="${classes}" part="badge" aria-label="${isDot ? '' : this.value}">
        ${isDot ? nothing : this.value}
      </span>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  override render() {
    return html`
      <slot @slotchange="${this._onSlotChange}"></slot>
      ${this._badge()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-badge': UwcBadge; }
}
